import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang, CATEGORIES, catLabel } from "../lib/i18n";
import { LangToggle } from "../components/LangToggle";
import { PhotoUploader } from "../components/PhotoUploader";
import api, { mediaUrl } from "../lib/api";
import { ArrowLeft, Loader2, MapPin, AlertTriangle } from "lucide-react";

const URGENCIES = [
  { id: "normale", color: "border-slate-200", active: "border-emerald-500 bg-emerald-50 text-emerald-700" },
  { id: "urgente", color: "border-slate-200", active: "border-amber-500 bg-amber-50 text-amber-700" },
  { id: "emergenza", color: "border-slate-200", active: "border-rose-500 bg-rose-50 text-rose-700" },
];

export default function NewRequest() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(params.get("cat") ? 1 : 0);
  const [busy, setBusy] = useState(false);
  const [zones, setZones] = useState(["Roma"]);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState({
    category: params.get("cat") || "", problem: "", description: "", photos: [],
    address: { street: "", city: "Roma", details: "" }, time_slot: "", urgency: "normale",
    name: user?.name || "", phone: user?.phone || "",
    lock_details: { door_state: "", issue: "", door_type: "", relationship: "" },
  });

  useEffect(() => { setForm((f) => ({ ...f, name: f.name || user?.name || "", phone: f.phone || user?.phone || "" })); }, [user]);
  useEffect(() => { api.get("/settings").then((r) => setZones(r.data.zones || ["Roma"])).catch(() => {}); }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setAddr = (k, v) => setForm({ ...form, address: { ...form.address, [k]: v } });
  const setLock = (k, v) => setForm({ ...form, lock_details: { ...form.lock_details, [k]: v } });
  const isLock = form.category === "porta_serratura";

  const steps = ["category", "problem", "photos", "address", "contact", "summary"];
  const total = steps.length;

  const canNext = () => {
    if (step === 0) return !!form.category;
    if (step === 1) return form.problem.trim() && form.description.trim() && (!isLock || (form.lock_details.door_state && form.lock_details.issue && form.lock_details.door_type && form.lock_details.relationship));
    if (step === 2) return form.photos.length >= 1;
    if (step === 3) return form.address.street.trim() && form.time_slot.trim();
    if (step === 4) return form.name.trim() && form.phone.trim();
    return true;
  };

  const submit = async () => {
    setBusy(true); setSubmitError("");
    try {
      const payload = { ...form };
      if (!isLock) delete payload.lock_details;
      const { data } = await api.post("/orders", payload);
      nav(`/ordine/${data.id}`, { replace: true });
    } catch (e) {
      setSubmitError(e.response?.data?.detail ? String(e.response.data.detail) : "Invio non riuscito. Riprova.");
    }
    finally { setBusy(false); }
  };

  const outOfZone = form.address.city && !zones.some((z) => {
    const c = form.address.city.trim().toLowerCase();
    const zz = z.trim().toLowerCase();
    return c.includes(zz) || zz.includes(c);
  });

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col bg-slate-50">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
        <button data-testid="nr-back" onClick={() => (step > 0 ? setStep(step - 1) : nav("/area-clienti"))} className="text-slate-500"><ArrowLeft /></button>
        <span className="text-sm font-bold text-slate-700">{t("step")} {step + 1} {t("of")} {total}</span>
        <LangToggle />
      </header>
      <div className="h-1 bg-slate-200"><div className="h-1 bg-blue-600 transition-all" style={{ width: `${((step + 1) / total) * 100}%` }} /></div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        {step === 0 && (
          <div className="animate-fade-up">
            <h2 className="font-display text-2xl font-extrabold text-slate-900">{t("categories")}</h2>
            <div className="mt-4 space-y-3">
              {CATEGORIES.map((c) => {
                const Icon = Icons[c.icon] || Icons.Wrench;
                const active = form.category === c.id;
                return (
                  <button key={c.id} data-testid={`nr-cat-${c.id}`} onClick={() => set("category", c.id)}
                    className={`flex w-full items-center gap-3 rounded-2xl border-2 bg-white p-4 text-left transition ${active ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Icon className="h-5 w-5" /></div>
                    <span className="text-sm font-bold text-slate-900">{c[lang]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="animate-fade-up space-y-4">
            <h2 className="font-display text-2xl font-extrabold text-slate-900">{catLabel(form.category, lang)}</h2>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">{t("problem")}</label>
              <input data-testid="nr-problem" value={form.problem} onChange={(e) => set("problem", e.target.value)} placeholder={t("problem_ph")}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">{t("description")}</label>
              <textarea data-testid="nr-description" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("description_ph")}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500" />
            </div>
            {isLock && (
              <div className="space-y-3 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="nr-lock-step">
                <p className="text-sm font-bold text-amber-900">{t("lock_details")}</p>
                <LockSelect label={t("door_state")} testid="lock-door_state" value={form.lock_details.door_state} onChange={(v) => setLock("door_state", v)} options={[["accostata", t("door_accostata")], ["chiusa_a_chiave", t("door_chiusa")]]} />
                <LockSelect label={t("lock_issue")} testid="lock-issue" value={form.lock_details.issue} onChange={(v) => setLock("issue", v)} options={[["chiavi_interno", t("issue_chiavi_interno")], ["smarrite", t("issue_smarrite")], ["spezzate", t("issue_spezzate")], ["serratura_bloccata", t("issue_serratura_bloccata")]]} />
                <LockSelect label={t("door_type")} testid="lock-door_type" value={form.lock_details.door_type} onChange={(v) => setLock("door_type", v)} options={[["normale", t("door_normale")], ["blindata", t("door_blindata")]]} />
                <LockSelect label={t("relationship")} testid="lock-relationship" value={form.lock_details.relationship} onChange={(v) => setLock("relationship", v)} options={[["proprietario", t("rel_proprietario")], ["inquilino", t("rel_inquilino")], ["ospite", t("rel_ospite")], ["gestore", t("rel_gestore")], ["altro", t("rel_altro")]]} />
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up">
            <h2 className="font-display text-2xl font-extrabold text-slate-900">{t("photos")}</h2>
            <p className="mb-4 mt-1 text-sm text-slate-500">{t("add_photo")} (1–5)</p>
            <PhotoUploader photos={form.photos} setPhotos={(p) => set("photos", p)} testid="nr-photos" />
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-up space-y-4">
            <h2 className="font-display text-2xl font-extrabold text-slate-900">{t("address")}</h2>
            <input data-testid="nr-street" value={form.address.street} onChange={(e) => setAddr("street", e.target.value)} placeholder={t("address_ph")}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500" />
            <input data-testid="nr-city" value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} placeholder={t("city")}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500" />
            {outOfZone && (
              <div data-testid="nr-outofzone" className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0" />{t("out_of_zone_warn")}
              </div>
            )}
            <textarea data-testid="nr-details" rows={3} value={form.address.details} onChange={(e) => setAddr("details", e.target.value)} placeholder={t("address_details_ph")}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500" />
            <input data-testid="nr-timeslot" value={form.time_slot} onChange={(e) => set("time_slot", e.target.value)} placeholder={t("time_slot")}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500" />
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">{t("urgency")}</label>
              <div className="flex gap-2">
                {URGENCIES.map((u) => (
                  <button key={u.id} data-testid={`nr-urgency-${u.id}`} onClick={() => set("urgency", u.id)}
                    className={`flex-1 rounded-xl border-2 py-2.5 text-xs font-bold transition ${form.urgency === u.id ? u.active : u.color + " text-slate-500 bg-white"}`}>
                    {t(`urgency_${u.id}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="animate-fade-up space-y-4">
            <h2 className="font-display text-2xl font-extrabold text-slate-900">{t("contact")}</h2>
            <input data-testid="nr-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("name")}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500" />
            <input data-testid="nr-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("phone")}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500" />
          </div>
        )}

        {step === 5 && (
          <div className="animate-fade-up space-y-3">
            <h2 className="font-display text-2xl font-extrabold text-slate-900">{t("summary")}</h2>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm">
              <Row label={t("categories")} value={catLabel(form.category, lang)} />
              <Row label={t("problem")} value={form.problem} />
              <Row label={t("description")} value={form.description} />
              <Row label={t("address")} value={`${form.address.street}, ${form.address.city}`} />
              <Row label={t("time_slot")} value={form.time_slot} />
              <Row label={t("urgency")} value={t(`urgency_${form.urgency}`)} />
              <Row label={t("contact")} value={`${form.name} · ${form.phone}`} />
            </div>
            {form.photos.length > 0 && (
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {form.photos.map((p, i) => <img key={i} src={mediaUrl(p)} alt="" className="h-20 w-20 rounded-xl object-cover" />)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4">
        {submitError && <p data-testid="nr-submit-error" className="mb-2 text-center text-sm font-semibold text-rose-600">{submitError}</p>}
        {step < 5 ? (
          <button data-testid="nr-next" disabled={!canNext()} onClick={() => setStep(step + 1)}
            className="w-full rounded-xl bg-blue-600 py-3 font-bold text-white transition hover:bg-blue-700 active:scale-[0.98] disabled:opacity-40">{t("next")}</button>
        ) : (
          <button data-testid="nr-submit" disabled={busy} onClick={submit}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 font-bold text-white transition hover:bg-emerald-700 active:scale-[0.98] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("send_request")}</button>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 border-b border-slate-100 py-2 last:border-0">
    <span className="shrink-0 font-semibold text-slate-500">{label}</span>
    <span className="text-right text-slate-800">{value || "—"}</span>
  </div>
);

const LockSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold text-amber-900">{label}</label>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-500">
      <option value="">—</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);
